import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, exhaustMap, map, of, switchMap, takeUntil } from 'rxjs';
import { CredentialTransitionManagementService } from '../../services/credential-transition-management.service';
import * as actions from './credential-transitions.actions';

@Injectable()
export class CredentialTransitionsEffects
{
  constructor(private actions$: Actions, private service: CredentialTransitionManagementService)
  {
  }

  loadList = createEffect(() => this.actions$.pipe(
    ofType(actions.loadEmployeeTransitions),
    switchMap(action => this.service.transitions(action.filter).pipe(
      takeUntil(this.actions$.pipe(ofType(actions.clearEmployeeTransitions))),
      map(list => actions.loadEmployeeTransitionsSuccess({ list })),
      catchError(error => of(this.failed(error)))
    ))
  ));

  loadLookups = createEffect(() => this.actions$.pipe(
    ofType(actions.loadEmployeeTransitionLookups),
    switchMap(() => this.service.transitionLookups().pipe(
      takeUntil(this.actions$.pipe(ofType(actions.clearEmployeeTransitions))),
      map(lookups => actions.loadEmployeeTransitionLookupsSuccess({ lookups })),
      catchError(error => of(this.failed(error)))
    ))
  ));

  loadDetail = createEffect(() => this.actions$.pipe(
    ofType(actions.loadEmployeeTransition),
    switchMap(action => this.service.transition(action.id).pipe(
      takeUntil(this.actions$.pipe(ofType(actions.clearEmployeeTransitions, actions.closeEmployeeTransition))),
      map(detail => actions.loadEmployeeTransitionSuccess({ detail })),
      catchError(error => of(this.failed(error)))
    ))
  ));

  save = createEffect(() => this.actions$.pipe(
    ofType(actions.updateEmployeeTransition),
    exhaustMap(action => this.service.updateTransition(action.id, action.model).pipe(
      map(detail => actions.employeeTransitionSaved({ detail, notice: 'Transition changes saved. Use Try advancement to check its next step.' })),
      catchError(error => of(this.failed(error)))
    ))
  ));

  advance = createEffect(() => this.actions$.pipe(
    ofType(actions.advanceEmployeeTransition),
    concatMap(action => this.service.advanceTransition(action.id, action.model).pipe(
      map(detail =>
      {
        if (detail.status.payDecreaseWarning && !action.model.confirmPayDecrease)
        {
          if (window.confirm("This offer will decrease the employee's current pay. Are you sure you want to generate and send it?"))
            return actions.advanceEmployeeTransition({ id: detail.id, model: {
              processVersionID: detail.processVersionID,
              attemptID: detail.status.attemptID,
              confirmPayDecrease: true
            } });

          return actions.employeeTransitionSaved({ detail, notice: 'Advancement stopped. The pay decrease was not confirmed.' });
        }

        return actions.employeeTransitionSaved({ detail, notice: detail.lastError
          ? 'Advancement failed. See the recorded error below.'
          : detail.status.message || 'Advancement checked. The latest status is shown below.' });
      }),
      catchError(error => of(this.failed(error)))
    ))
  ));

  private failed(error: any)
  {
    return actions.employeeTransitionFailed({ error: typeof error?.error === 'string'
      ? error.error : error?.message || 'The request failed. Refresh and try again.' });
  }
}
